import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, ArrowRight, ArrowLeft } from 'lucide-react';

const PAD = 8;
const CARD_H = 230;

const useTargetRect = (testId) => {
  const [rect, setRect] = useState(null);
  const measure = useCallback(() => {
    const el = document.querySelector(`[data-testid="${testId}"]`);
    setRect(el ? el.getBoundingClientRect() : null);
  }, [testId]);
  useLayoutEffect(() => {
    const scrollToTarget = () => {
      const el = document.querySelector(`[data-testid="${testId}"]`);
      if (!el) return;
      const r = el.getBoundingClientRect();
      window.scrollTo({ top: Math.max(0, window.scrollY + r.top - window.innerHeight / 4), behavior: 'smooth' });
    };
    scrollToTarget();
    const timers = [setTimeout(scrollToTarget, 350), setTimeout(measure, 150), setTimeout(measure, 800)];
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => { timers.forEach(clearTimeout); window.removeEventListener('resize', measure); window.removeEventListener('scroll', measure, true); };
  }, [testId, measure]);
  return rect;
};

const TourCard = ({ step, idx, total, onNext, onBack, onSkip, style }) => {
  const { t } = useTranslation();
  const last = idx === total - 1;
  return (
    <div className="fixed z-[1002] bg-white rounded-sm shadow-2xl border-t-4 border-[#b8893d] p-5 w-[calc(100vw-32px)] max-w-sm transition-[top,left] duration-300" style={style} data-testid="welcome-tour-card" role="dialog" aria-live="polite">
      <div className="flex items-start justify-between gap-3">
        <p className="text-[10px] tracking-[0.28em] uppercase text-[#b8893d]" data-testid="welcome-tour-progress">{t('tour.progress', { n: idx + 1, total })}</p>
        <button type="button" onClick={onSkip} className="text-[#7a8a99] hover:text-[#1B3A5C]" aria-label={t('tour.skip')} data-testid="welcome-tour-close"><X className="w-4 h-4" /></button>
      </div>
      <h3 className="font-serif text-xl text-[#1B3A5C] mt-1" data-testid="welcome-tour-title">{t(`tour.steps.${step.key}.title`)}</h3>
      <p className="text-sm text-[#34536b] mt-2 leading-relaxed">{t(`tour.steps.${step.key}.desc`)}</p>
      <div className="flex items-center justify-between gap-2 mt-5">
        <button type="button" onClick={onSkip} className="text-xs tracking-wider text-[#7a8a99] hover:text-[#1B3A5C] underline" data-testid="welcome-tour-skip">{t('tour.skip')}</button>
        <div className="flex gap-2">
          {idx > 0 && <button type="button" onClick={onBack} className="px-3 py-2 text-xs tracking-wider border border-gray-200 rounded-sm text-[#1B3A5C] hover:border-[#b8893d] flex items-center gap-1" data-testid="welcome-tour-back"><ArrowLeft className="w-3.5 h-3.5" />{t('tour.back')}</button>}
          <button type="button" onClick={onNext} className="px-4 py-2 text-xs tracking-wider bg-[#1B3A5C] hover:bg-[#15304d] text-white rounded-sm flex items-center gap-1" data-testid="welcome-tour-next">{last ? t('tour.done') : t('tour.next')}{!last && <ArrowRight className="w-3.5 h-3.5" />}</button>
        </div>
      </div>
    </div>
  );
};

const cardPosition = (rect) => {
  const w = Math.min(384, window.innerWidth - 32);
  if (!rect) return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
  const left = Math.max(16, Math.min(rect.left, window.innerWidth - w - 16));
  const maxTop = Math.max(16, window.innerHeight - CARD_H - 16);
  const below = rect.bottom + PAD + 12;
  const top = below + CARD_H < window.innerHeight ? below : rect.top - PAD - 12 - CARD_H;
  return { top: Math.min(maxTop, Math.max(16, top)), left };
};

export const WelcomeTour = ({ steps, onFinish }) => {
  const [idx, setIdx] = useState(0);
  const step = steps[idx];
  const rect = useTargetRect(step.target);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onFinish(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onFinish]);
  const next = () => (idx === steps.length - 1 ? onFinish(true) : setIdx(idx + 1));
  const skip = () => onFinish(false);
  return (
    <div data-testid="welcome-tour">
      <div className="fixed inset-0 z-[1000]" onClick={(e) => e.stopPropagation()} />
      {rect && (
        <div className="fixed z-[1001] rounded-md ring-2 ring-[#e0b56a] pointer-events-none transition-all duration-300"
          style={{ top: rect.top - PAD, left: rect.left - PAD, width: rect.width + PAD * 2, height: rect.height + PAD * 2, boxShadow: '0 0 0 9999px rgba(15, 39, 64, 0.72)' }}
          data-testid="welcome-tour-spotlight" />
      )}
      <TourCard step={step} idx={idx} total={steps.length} onNext={next} onBack={() => setIdx(idx - 1)} onSkip={skip} style={cardPosition(rect)} />
    </div>
  );
};
